/* eslint-env es6 */
const AUTOFILL_START = 'asc-autofill-start';
const AUTOFILL_CANCEL = 'asc-autofill-cancel';
const AUTOFILL_ANIMATIONS = [AUTOFILL_START, AUTOFILL_CANCEL];

/** @type {(event: AnimationEvent) => boolean} */
const isAutofillEvent = event => AUTOFILL_ANIMATIONS.includes(event.animationName);

/**
 * An interface for observing browser autofill on input elements. Currently
 * limited to Chromium-based browsers.
 */
export class AutofillObserver {
  /**
   * Create an `AutofillObserver` with the given `callback`. The observer
   * will call the callback with an array of `AutofillEntry` objects whenever
   * browser autofill changes are detected in any of its observed targets.
   *
   * The second argument passed to the callback is this observer instance
   * itself. This is useful for calling `AutofillObserver#disconnect`, for
   * instance, at the end of the callback.
   *
   * @param {AutofillObserverCallback} callback
   */
  constructor(callback) {
    this.callback = callback;
    /** @type {Map<Element, AutofillEventHandler>} */
    this.handlers = new Map();
  }

  /**
   * Observe the given `target` for autofill changes. When browser provides
   * an autofill value to the target, or the user edits the target and
   * cancels the autofill, the observer will call its callback with an entry
   * for the target.
   *
   * This function accepts an optional second argument, `options`. Currently
   * there is only one option: `subtree`. If `subtree` is true, the observer
   * will also watch for autofill events anywhere in the descendants of
   * `target`. This is useful for observing all autofill changes in a form
   * or document, like so:
   *
   * ```js
   * const observer = new AutofillObserver(callback);
   * observer.observe(document.documentElement, { subtree: true })
   * ```
   *
   * @param {Element} target
   * @param {AutofillObserverOptions} [options]
   * @returns {void}
   */
  observe(target) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const {
      subtree
    } = Object.assign({
      subtree: false
    }, options);

    /** @type {AutofillEventHandler} */
    const handler = event => {
      if (!isAutofillEvent(event)) {
        return;
      }
      const target = subtree ? event.target : event.currentTarget;
      const isAutofilled = event.animationName === AUTOFILL_START;

      /** @type {AutofillEntry[]} */
      const entries = [{
        target,
        isAutofilled
      }];
      this.callback.call(null, entries, this);
    };
    target.addEventListener('animationstart', handler);
    this.handlers.set(target, handler);
  }

  /**
   * Stop listening for autofill changes to the given `target`. If the
   * target is not currently being observed, this method does nothing.
   * @param {Element} target
   * @returns {void}
   */
  unobserve(target) {
    if (!this.handlers.has(target)) {
      return;
    }
    const handler = this.handlers.get(target);
    target.removeEventListener('animationstart', handler);
    this.handlers.delete(target);
  }

  /**
   * Stop listening for autofill changes to all targets until `observe` is
   * called again.
   */
  disconnect() {
    for (const [target, handler] of this.handlers) {
      target.removeEventListener(handler);
    }
    this.handlers.clear();
  }
}

/**
 * @callback AutofillObserverCallback
 * @param {AutofillEntry[]} entries
 * @param {AutofillObserver} observer
 * @returns {void}
 */

/**
 * @typedef {object} AutofillEntry
 * @prop {Element} target
 * @prop {boolean} isAutofilled
 */

/**
 * @typedef {object} AutofillObserverOptions
 * @prop {boolean} [subtree]
 */

/**
 * @callback AutofillEventHandler
 * @param {AnimationEvent} event
 * @returns {void}
 */